import { Mark, RawCommands, Editor, CommandProps } from '@tiptap/core';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { SelectChangeEvent } from '@mui/material/Select';
import React, { ReactElement } from 'react';

interface ClassExtensionOptions {
  types: string[];
  styles: Array<{ value: string; label: string }>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customInlineStyle: {
      setCustomInlineStyle: (className: string) => ReturnType;
      removeCustomInlineStyle: () => ReturnType;
    };
  }
}

const CustomInlineStyleExtension = Mark.create<ClassExtensionOptions>({
  name: 'customInlineStyle',
  addOptions() {
    return {
      types: ['textStyle'],
      styles: [],
    };
  },
  parseHTML() {
    return [
      {
        tag: 'span[class]',
        getAttrs: element => {
          if (!(element instanceof HTMLElement)) {
            return false;
          }
          return { class: element.getAttribute('class') };
        },
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0];
  },
  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: element => element.getAttribute('class'),
        renderHTML: attributes => {
          if (!attributes.class) {
            return {};
          }
          return { class: attributes.class };
        },
      },
    };
  },
  addCommands() {
    return {
      setCustomInlineStyle:
        (className: string) =>
        ({ chain }: CommandProps) => {
          return chain()
            .setMark(this.name, { class: className })
            .run();
        },
      removeCustomInlineStyle:
        () =>
        ({ chain }: CommandProps) => {
          return chain()
            .unsetMark(this.name)
            .run();
        },
    };
  },
});

interface CustomInlineStyleControlProps {
  editor: Editor | null | undefined;
  styles: Array<{ value: string; label: string }>;
}

const CustomInlineStyleControl = ({
  editor,
  styles,
}: CustomInlineStyleControlProps): ReactElement | null => {
  if (!editor) {
    return null;
  }

  const currentClass = editor.isActive('customInlineStyle')
    ? editor.getAttributes('customInlineStyle').class
    : '';

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    if (!editor) return;

    if (!value) {
      editor.commands.removeCustomInlineStyle();
      return;
    }
    editor.commands.setCustomInlineStyle(value);
  };

  return (
    <Select
      value={currentClass || ''}
      onChange={handleChange}
      size="small"
      sx={{
        minWidth: 120,
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none', // Remove default border
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          border: '1px solid rgba(0, 0, 0, 0.23)', // Show border on hover
        },
        '& .MuiSelect-select': {
          padding: '4px 14px', // Reduce padding (default is 8px 14px for size="small")
        }
      }}
      displayEmpty
      renderValue={(value) => {
        if (!value) {
          return <em>inline style</em>;
        }
        return value;
      }}
    >
      <MenuItem value="">
        <em>None</em>
      </MenuItem>
      {styles.map(style => (
        <MenuItem key={style.value} value={style.value}>
          {style.label}
        </MenuItem>
      ))}
    </Select>
  );
};

export type { ClassExtensionOptions };
export { CustomInlineStyleExtension, CustomInlineStyleControl };